import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import { formatCurrency, formatDate, today } from '../utils/helpers';

const empty = {
  payment_date: today(),
  payment_type: 'received',
  ledger_id: '',
  reference_type: 'sale',
  reference_id: '',
  amount: 0,
  payment_mode: 'cash',
  bank_id: '',
  bank_name: '',
  cheque_no: '',
  transaction_id: '',
  notes: ''
};

export default function Payments() {
  const [list, setList] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get('/payments').then(r => setList(r.data.data || []));
  useEffect(() => {
    load();
    api.get('/ledgers').then(r => setLedgers((r.data.data||[]).filter(l => l.status == 1)));
    api.get('/sales').then(r => setSales((r.data.data||[]).filter(s => s.payment_status !== 'paid')));
    api.get('/purchases').then(r => setPurchases((r.data.data||[]).filter(p => p.payment_status !== 'paid')));
    api.get('/banks').then(r => setBanks((r.data.data||[]).filter(b => b.status == 1)));
  }, []);

  const save = async () => {
    if (!form.amount || form.amount <= 0) return toast.error('Amount required');
    if (form.payment_mode !== 'cash' && !form.bank_id) return toast.error('Select a bank for non-cash payments');
    try {
      await api.post('/payments', form);
      toast.success('Payment recorded');
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete payment? Invoice & bank balance will be reverted.')) return;
    try { await api.delete('/payments/' + id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Error'); }
  };

  // For "received": show ledgers with party_type customer/both, plus open sales for the chosen ledger.
  // For "paid": show ledgers with party_type supplier/both, plus open purchases.
  const isReceived = form.payment_type === 'received';
  const filteredLedgers = ledgers.filter(l => isReceived
    ? ['customer','both'].includes(l.party_type)
    : ['supplier','both'].includes(l.party_type));
  const invoices = isReceived
    ? sales.filter(s => s.ledger_id == form.ledger_id)
    : purchases.filter(p => p.ledger_id == form.ledger_id);

  const received = list.filter(p => p.payment_type === 'received').reduce((s, r) => s + parseFloat(r.amount||0), 0);
  const paid = list.filter(p => p.payment_type === 'paid').reduce((s, r) => s + parseFloat(r.amount||0), 0);

  return (
    <div className="card">
      <div className="card-header">
        <h3>Payments ({list.length}) — Received: {formatCurrency(received)} · Paid: {formatCurrency(paid)}</h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={14}/> New Payment</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Payment No</th><th>Date</th><th>Type</th>
              <th>Ledger</th><th>Reference</th><th>Mode</th>
              <th>Bank</th><th className="td-num">Amount</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length ? list.map(p => (
              <tr key={p.id}>
                <td><strong>{p.payment_no}</strong></td>
                <td>{formatDate(p.payment_date)}</td>
                <td><span className={'badge ' + (p.payment_type==='received'?'badge-green':'badge-red')}>{p.payment_type}</span></td>
                <td>{p.ledger_name || '-'}</td>
                <td>{p.reference_type} {p.reference_id ? '#'+p.reference_id : ''}</td>
                <td><span className="badge badge-blue">{p.payment_mode}</span></td>
                <td>{p.bank_acc_name || '-'}</td>
                <td className="td-num">{formatCurrency(p.amount)}</td>
                <td><button className="btn btn-sm btn-red" onClick={()=>del(p.id)}><Trash2 size={12}/></button></td>
              </tr>
            )) : <tr><td colSpan="9" className="empty">No payments</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title="New Payment / Receipt" size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>Save</button>
        </>}>
        <div className="form-grid">
          <div className="form-group">
            <label>Payment Type *</label>
            <select value={form.payment_type} onChange={e=>setForm({
              ...form,
              payment_type: e.target.value,
              ledger_id: '',
              reference_id: '',
              reference_type: e.target.value === 'received' ? 'sale' : 'purchase'
            })}>
              <option value="received">Received (from Customer Ledger)</option>
              <option value="paid">Paid (to Supplier Ledger)</option>
            </select>
          </div>
          <div className="form-group">
            <label>Date *</label>
            <input type="date" value={form.payment_date} onChange={e=>setForm({...form, payment_date:e.target.value})}/>
          </div>
          <div className="form-group">
            <label>Ledger</label>
            <select value={form.ledger_id} onChange={e=>setForm({...form, ledger_id:e.target.value, reference_id:''})}>
              <option value="">-- Select Ledger --</option>
              {filteredLedgers.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Against Invoice</label>
            <select value={form.reference_id} onChange={e=>{
              const inv = invoices.find(x => x.id == e.target.value);
              setForm({...form, reference_id: e.target.value, amount: inv ? inv.due_amount : form.amount});
            }}>
              <option value="">-- Advance / General --</option>
              {invoices.map(i=><option key={i.id} value={i.id}>{i.invoice_no} — Due {formatCurrency(i.due_amount)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Amount *</label>
            <input type="number" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})}/>
          </div>
          <div className="form-group">
            <label>Payment Mode</label>
            <select value={form.payment_mode} onChange={e=>setForm({...form, payment_mode:e.target.value, bank_id: e.target.value==='cash'?'':form.bank_id})}>
              <option value="cash">Cash</option>
              <option value="bank">Bank</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
          {form.payment_mode !== 'cash' && (
            <div className="form-group"><label>Bank Account *</label>
              <select value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})}>
                <option value="">-- Select Bank --</option>
                {banks.filter(b => b.account_type !== 'cash').map(b=><option key={b.id} value={b.id}>{b.bank_name} — {formatCurrency(b.current_balance)}</option>)}
              </select>
            </div>
          )}
          {form.payment_mode === 'cash' && (
            <div className="form-group"><label>Cash Account</label>
              <select value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})}>
                <option value="">-- Select (optional) --</option>
                {banks.filter(b => b.account_type === 'cash').map(b=><option key={b.id} value={b.id}>{b.bank_name} — {formatCurrency(b.current_balance)}</option>)}
              </select>
            </div>
          )}
          {form.payment_mode === 'cheque' && <div className="form-group"><label>Cheque No</label><input value={form.cheque_no} onChange={e=>setForm({...form, cheque_no:e.target.value})}/></div>}
          {(form.payment_mode === 'upi' || form.payment_mode === 'bank') && <div className="form-group"><label>Transaction ID</label><input value={form.transaction_id} onChange={e=>setForm({...form, transaction_id:e.target.value})}/></div>}
          <div className="form-group full-width"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
